/**
 * LV Product Service
 * Handles all API interactions with the lv_products table
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { LVProduct } from '../types';
import { CreateLVProductSchema, UpdateLVProductSchema } from '../schemas/validation';

interface CreateLVProductDto {
  floor_plan_id: string;
  product_id?: string;
  category: string;
  subcategory?: string;
  name: string;
  x_position: number;
  y_position: number;
  rotation?: number;
  scale?: number;
  parent_group_id?: string;
  room_id?: string;
  ip_address?: string;
  mac_address?: string;
  serial_number?: string;
  installation_notes?: string;
  metadata?: Record<string, any>;
  category_info?: {
    category: string;
    subcategory?: string;
    custom_icon?: string;
    display_label?: string;
    is_generic?: boolean;
  };
}

type UpdateLVProductDto = Partial<Omit<CreateLVProductDto, 'floor_plan_id'>>;

class LVProductService {
  private async client() {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Get all LV products for a floor plan
   */
  async getByFloorPlan(floorPlanId: string): Promise<LVProduct[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .eq('floor_plan_id', floorPlanId)
      .order('created_at', { ascending: true });

    if (error) {
      logger.error('Error fetching LV products:', error);
      throw new Error(`Failed to fetch LV products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get LV products by room
   */
  async getByRoom(roomId: string): Promise<LVProduct[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .eq('room_id', roomId)
      .order('name', { ascending: true });

    if (error) {
      logger.error('Error fetching LV products by room:', error);
      throw new Error(`Failed to fetch LV products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get a single LV product by ID
   */
  async getById(id: string): Promise<LVProduct | null> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      logger.error('Error fetching LV product:', error);
      throw new Error(`Failed to fetch LV product: ${error.message}`);
    }

    return data;
  }

  /**
   * Create a new LV product instance
   *
   * Automatically applies saved default scales if:
   * 1. No scale is explicitly provided in dto
   * 2. A default exists for this category/subcategory
   *
   * Priority order: subcategory default > category default > 1.0
   */
  async create(dto: CreateLVProductDto): Promise<LVProduct> {
    const validated = CreateLVProductSchema.parse(dto);

    // Check for default scale if not provided or if using default 1.0
    let finalScale = validated.scale;
    if (!finalScale || finalScale === 1.0) {
      try {
        // Import here to avoid circular dependencies
        const elementSizeDefaults = await import('@/services/elementSizeDefaultsService');

        const category = dto.category_info?.category || validated.category;
        const subcategory = dto.category_info?.subcategory || validated.subcategory;

        // Try to get default scale (subcategory has priority over category)
        const defaultScale = await elementSizeDefaults.getDefaultScale(
          'product',
          category,
          subcategory || undefined,
        );

        if (defaultScale) {
          finalScale = defaultScale;
          logger.info(
            `Applied default scale ${defaultScale} for ${category}${subcategory ? `/${subcategory}` : ''}`,
          );
        }
      } catch (error) {
        // If fetching defaults fails, just log and continue without defaults
        logger.warn('Failed to fetch default scale:', error);
      }
    }

    // If category_info is provided, ensure backward compatibility
    const insertData = {
      ...validated,
      scale: finalScale, // Apply the final scale (default or provided)
      // Keep legacy fields for backward compatibility
      category: dto.category_info?.category || validated.category,
      subcategory: dto.category_info?.subcategory || validated.subcategory,
      // Store new category_info if provided
      category_info: dto.category_info || null,
    };

    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .insert(insertData)
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .single();

    if (error) {
      logger.error('Error creating LV product:', error);
      throw new Error(`Failed to create LV product: ${error.message}`);
    }

    return data;
  }

  /**
   * Update an existing LV product
   */
  async update(id: string, dto: UpdateLVProductDto): Promise<LVProduct> {
    const validated = UpdateLVProductSchema.parse(dto);

    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .update(validated)
      .eq('id', id)
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .single();

    if (error) {
      logger.error('Error updating LV product:', error);
      throw new Error(`Failed to update LV product: ${error.message}`);
    }

    return data;
  }

  /**
   * Update position of an LV product
   */
  async updatePosition(id: string, x: number, y: number): Promise<LVProduct> {
    return this.update(id, { x_position: x, y_position: y });
  }

  /**
   * Delete an LV product
   */
  async delete(id: string): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase.from('lv_products').delete().eq('id', id);

    if (error) {
      logger.error('Error deleting LV product:', error);
      throw new Error(`Failed to delete LV product: ${error.message}`);
    }
  }

  /**
   * Batch create LV products
   */
  async createBatch(products: CreateLVProductDto[]): Promise<LVProduct[]> {
    const validated = products.map(p => CreateLVProductSchema.parse(p));

    const supabase = await this.client();
    const { data, error } = await supabase.from('lv_products').insert(validated).select(`
        *,
        product:product_library(*)
      `);

    if (error) {
      logger.error('Error creating LV products batch:', error);
      throw new Error(`Failed to create LV products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get children of a group/rack
   */
  async getChildren(parentGroupId: string): Promise<LVProduct[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .select(
        `
        *,
        product:product_library(*)
      `,
      )
      .eq('parent_group_id', parentGroupId)
      .order('name', { ascending: true });

    if (error) {
      logger.error('Error fetching group children:', error);
      throw new Error(`Failed to fetch group children: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Move products to a different room
   */
  async moveToRoom(productIds: string[], roomId: string | null): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from('lv_products')
      .update({ room_id: roomId })
      .in('id', productIds);

    if (error) {
      logger.error('Error moving products to room:', error);
      throw new Error(`Failed to move products: ${error.message}`);
    }
  }

  /**
   * Lock/unlock products
   */
  async setLocked(productIds: string[], isLocked: boolean): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from('lv_products')
      .update({ is_locked: isLocked })
      .in('id', productIds);

    if (error) {
      logger.error('Error updating product lock status:', error);
      throw new Error(`Failed to update lock status: ${error.message}`);
    }
  }

  /**
   * Clone an LV product
   */
  async clone(id: string, newPosition?: { x: number; y: number }): Promise<LVProduct> {
    const original = await this.getById(id);
    if (!original) {
      throw new Error('Product not found');
    }

    const { id: _, created_at, updated_at, ...cloneData } = original;

    return this.create({
      ...cloneData,
      name: `${cloneData.name} (Copy)`,
      x_position: newPosition?.x || cloneData.x_position + 50,
      y_position: newPosition?.y || cloneData.y_position + 50,
    });
  }
}

export const lvProductService = new LVProductService();
